import { db } from './db';
import { CONTENT_VERSION, STORY_CONTENT_VERSION } from '@/domain/tasks/interpretation/content';

export interface LearningResponse {
  itemId: string;
  presented: unknown;
  choiceOrder: string[];
  selected: string;
  correct: boolean;
  responseMs: number;
  fragmentAttempts: string[];
  readingMs: number | null;
  answeredAt: string;
}
export interface LearningSession {
  id: string;
  participantCode: string;
  mode: 'traditional' | 'roleplay';
  episodeId: string;
  contentVersion: string;
  protocolVersion: string;
  contentStatus: 'demo-unvalidated';
  appVersion: string;
  seed: number;
  total: number;
  startedAt: string;
  endedAt: string | null;
  status: 'in-progress' | 'completed';
  responses: LearningResponse[];
  contentSnapshot: unknown;
}
export async function createLearningSession(mode: LearningSession['mode'], episodeId: string, total: number, participantCode: string, snapshot: unknown, contentVersion?: string): Promise<LearningSession> {
  const record: LearningSession = {
    id: crypto.randomUUID(), participantCode: participantCode.trim() || 'P000', mode, episodeId,
    contentVersion: contentVersion ?? (mode === 'roleplay' ? STORY_CONTENT_VERSION : CONTENT_VERSION), protocolVersion: 'interpretation-demo-1.0.0',
    contentStatus: 'demo-unvalidated', appVersion: __APP_VERSION__,
    seed: crypto.getRandomValues(new Uint32Array(1))[0] ?? 0,
    total, startedAt: new Date().toISOString(), endedAt: null, status: 'in-progress',
    responses: [], contentSnapshot: snapshot,
  };
  await db.learningSessions.add(record);
  return record;
}
/** A linked daily exercise has one record, including during StrictMode remounts. */
export async function getOrCreateDailyLearning(episodeId: string, total: number, participantCode: string, snapshot: unknown, contentVersion?: string): Promise<LearningSession> {
  return db.transaction('rw', db.learningSessions, async () => {
    const found = await db.learningSessions.where('episodeId').equals(episodeId).filter(r => r.participantCode === (participantCode.trim() || 'P000') && r.contentVersion === (contentVersion ?? CONTENT_VERSION)).first();
    return found ?? createLearningSession('traditional', episodeId, total, participantCode, snapshot, contentVersion);
  });
}
export async function findUnfinished(mode: LearningSession['mode'], episodeId: string, participantCode: string) {
  return db.learningSessions.orderBy('startedAt').reverse().filter(r =>
    r.mode === mode && r.episodeId === episodeId && r.participantCode === (participantCode.trim() || 'P000') &&
    r.status === 'in-progress' && r.contentVersion === (mode === 'roleplay' ? STORY_CONTENT_VERSION : CONTENT_VERSION),
  ).first();
}
/** Idempotent write at scene boundaries; do not show a saved result before this commits. */
export async function saveLearningResponse(record: LearningSession, response: LearningResponse): Promise<LearningSession> {
  return db.transaction('rw', db.learningSessions, async () => {
    const current = await db.learningSessions.get(record.id);
    if (!current) throw new Error('진행 기록을 찾을 수 없습니다. 처음부터 다시 시작해 주세요.');
    if (current.responses.some(r => r.itemId === response.itemId)) return current;
    const responses = [...current.responses, response];
    const completed = responses.length >= current.total;
    const next: LearningSession = { ...current, responses, status: completed ? 'completed' : 'in-progress', endedAt: completed ? new Date().toISOString() : null };
    await db.learningSessions.put(next);
    return next;
  });
}
